import React, { useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth-context";

// Self-service page for any logged-in user (admin or student) to change
// their own name/email and password. Exists so the site owner can move off
// the seeded admin@insidelms.test / AdminPass123 demo credentials without
// needing direct database access.
export default function AccountSettingsPage() {
  const { user, refresh } = useAuth();

  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "" });
  const [profileBusy, setProfileBusy] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSaved, setProfileSaved] = useState(false);

  const [pw, setPw] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState("");
  const [pwSaved, setPwSaved] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileBusy(true);
    setProfileError("");
    setProfileSaved(false);
    try {
      await api.put("/api/account", profile);
      await refresh();
      setProfileSaved(true);
    } catch (err) {
      setProfileError(err.message);
    } finally {
      setProfileBusy(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    setPwSaved(false);
    if (pw.newPassword !== pw.confirmPassword) {
      setPwError("New password and confirmation don't match");
      return;
    }
    setPwBusy(true);
    try {
      await api.put("/api/account/password", {
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      setPw({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPwSaved(true);
    } catch (err) {
      setPwError(err.message);
    } finally {
      setPwBusy(false);
    }
  };

  if (!user) return null;

  return (
    <div className="page page-narrow">
      <h1>Account settings</h1>

      <div className="auth-card">
        <h2 style={{ marginTop: 0 }}>Name &amp; email</h2>
        {profileError && <div className="alert alert-error">{profileError}</div>}
        {profileSaved && <div className="alert alert-success">Saved.</div>}
        <form onSubmit={saveProfile}>
          <label>
            Name
            <input
              value={profile.name}
              onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
              required
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={profile.email}
              onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
              required
            />
          </label>
          <button className="btn btn-primary btn-block" disabled={profileBusy}>
            {profileBusy ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>

      <div className="auth-card" style={{ marginTop: 20 }}>
        <h2 style={{ marginTop: 0 }}>Change password</h2>
        {pwError && <div className="alert alert-error">{pwError}</div>}
        {pwSaved && <div className="alert alert-success">Password changed.</div>}
        <form onSubmit={savePassword}>
          <label>
            Current password
            <input
              type="password"
              autoComplete="current-password"
              value={pw.currentPassword}
              onChange={(e) => setPw((p) => ({ ...p, currentPassword: e.target.value }))}
              required
            />
          </label>
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={pw.newPassword}
              onChange={(e) => setPw((p) => ({ ...p, newPassword: e.target.value }))}
              required
            />
          </label>
          <label>
            Confirm new password
            <input
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={pw.confirmPassword}
              onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))}
              required
            />
          </label>
          <button className="btn btn-primary btn-block" disabled={pwBusy}>
            {pwBusy ? "Saving…" : "Change password"}
          </button>
        </form>
      </div>
    </div>
  );
}
